import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '../../shared/services/api.service';
import { take, firstValueFrom } from 'rxjs';
import { VoiceFallbackDialogComponent } from '../voice-to-text-fallback/voice-fallback-dialog.component';
import { FallbackAnalyticsWidgetComponent } from '../voice-to-text-fallback/fallback-analytics-widget.component';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { Router } from '@angular/router';

// Bedrock/Nova Sonic content block, e.g. { text: '...' }
interface ContentBlock {
  text?: string;
  message?: string;
  type?: string;
  timestamp?: string;
  [key: string]: unknown;
}

type MessageContent = string | ContentBlock | ContentBlock[];

interface Message {
  role: 'user' | 'assistant' | 'system' | 'agent';
  content: MessageContent;
  timestamp?: string;
}

interface Transcript {
  pk: string;
  sk: string;
  connectionId: string;
  planNumber: string;
  productType?: string;
  brand?: string;
  messageHistory: Message[];
  chatDuration: number;
  audioIdleKeepAliveFired: number;
  rating?: string;
}

// As received from the API: messageHistory may still be a JSON string
interface RawTranscript extends Omit<Transcript, 'messageHistory'> {
  messageHistory: Message[] | string;
}

interface TranscriptResponseBody {
  data: RawTranscript[];
  lastEvaluatedKey?: string;
  totalCount: number;
  averageRating?: number | string | null;
  ratingCount?: number | string | null;
}

interface TranscriptResponse {
  statusCode: number;
  body: TranscriptResponseBody;
}

interface ExportColumn {
  id: string;
  label: string;
  selected: boolean;
}

const PRODUCT_TYPES = [
  { value: 'washing-machine', label: 'Washing Machine' },
  { value: 'dishwasher', label: 'Dishwasher' },
  { value: 'boiler', label: 'Boiler' },
  { value: 'oven', label: 'Oven' },
  { value: 'tumble-dryer', label: 'Tumble Dryer' },
  { value: 'fridge-freezer', label: 'Fridge Freezer' },
  { value: 'electric-cooker', label: 'Electric Cooker' },
  { value: 'gas-cooker', label: 'Gas Cooker' },
  { value: 'microwave', label: 'Microwave' },
  { value: 'tv', label: 'TV' },
];

const BRANDS = [
  { value: 'Domestic and General', label: 'Domestic and General' },
  { value: 'Other', label: 'Other' },
];

@Component({
  selector: 'app-voice2voice-transcripts',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    FallbackAnalyticsWidgetComponent,
    PageToolbarComponent,
  ],
  templateUrl: './voice2voice-transcripts.component.html',
  styleUrls: ['./voice2voice-transcripts.component.scss'],
})
export class Voice2VoiceTranscriptsComponent {
  private apiService = inject(ApiService);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  // UI State
  isLoading = signal(false);
  hasSearchedOnce = signal(false);
  errorMessage = signal<string | null>(null); // NEW: Error state
  currentPage = signal(1);
  totalCount = signal(0);
  selectedTranscript = signal<Transcript | null>(null);

  // Data
  transcripts = signal<Transcript[]>([]);
  displayedColumns = signal([
    'customerId',
    'date',
    'planNumber',
    'productType',
    'brand',
    'duration',
    'messageCount',
    'rating',

    'actions',
  ]);

  // Filter State
  filters = signal({
    productType: '',
    brand: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    limit: 10,
  });

  // Pagination state
  previousPages = signal<string[]>([]);
  lastEvaluatedKey = signal<string | null>(null);

  // Constants
  productTypes = PRODUCT_TYPES;
  brands = BRANDS;

  // Smart Export state
  isExporting = signal(false);
  exportProgress = signal('');
  exportStartDate: Date | null = null;
  exportEndDate: Date | null = null;
  averageUserRating = signal<number | null>(null);
  userRatingCount = signal(0);
  exportColumns = signal<ExportColumn[]>([
    { id: 'pk', label: 'Customer ID', selected: false },
    { id: 'planNumber', label: 'Plan Number', selected: false },
    { id: 'connectionId', label: 'Connection ID', selected: false },
    { id: 'rating', label: 'Rating', selected: true },
    { id: 'sk', label: 'Date & Time', selected: true },
    { id: 'productType', label: 'Product Type', selected: true },
    { id: 'brand', label: 'Brand', selected: true },
    { id: 'chatDuration', label: 'Duration', selected: true },
    { id: 'messageCount', label: 'Message Count', selected: true },
    { id: 'messageHistory', label: 'Message History', selected: true },
  ]);

  constructor() {
    // Set default date range (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    this.filters.update((f) => ({
      ...f,
      startDate,
      endDate,
    }));

    this.exportStartDate = new Date(startDate);
    this.exportEndDate = new Date(endDate);
  }

  backToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  onFilterChange() {
    // Reset pagination when filters change
    this.previousPages.set([]);
    this.lastEvaluatedKey.set(null);
    this.currentPage.set(1);
    this.errorMessage.set(null); // Clear error message
  }

  applyFilters() {
    this.fetchTranscripts();
  }

  clearFilters() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    this.filters.set({
      productType: '',
      brand: '',
      startDate,
      endDate,
      limit: 10,
    });

    this.previousPages.set([]);
    this.lastEvaluatedKey.set(null);
    this.currentPage.set(1);
    this.transcripts.set([]);
    this.averageUserRating.set(null);
    this.userRatingCount.set(0);
    this.hasSearchedOnce.set(false);
    this.errorMessage.set(null); // Clear error message
  }

  fetchTranscripts() {
    this.isLoading.set(true);

    const queryParams: Record<string, string> = {};

    if (this.filters().startDate) {
      queryParams['startDate'] = this.filters()
        .startDate!.toISOString()
        .split('T')[0];
    }

    if (this.filters().endDate) {
      // FIX: Include full day by using end-of-day timestamp
      // This ensures string comparison in DynamoDB includes all timestamps on the selected date
      // Without this, "2026-07-20" < "2026-07-20T12:37:23.167Z" fails due to string comparison
      const endDate = new Date(this.filters().endDate!);
      endDate.setHours(23, 59, 59, 999); // End of day
      queryParams['endDate'] = endDate.toISOString();
    }

    if (this.filters().productType) {
      queryParams['productType'] = this.filters().productType;
    }

    if (this.filters().brand) {
      queryParams['brand'] = this.filters().brand;
    }

    queryParams['limit'] = String(this.filters().limit);

    if (this.lastEvaluatedKey()) {
      queryParams['lastEvaluatedKey'] = this.lastEvaluatedKey()!;
    }

    this.apiService
      .get<TranscriptResponse | TranscriptResponseBody>(
        'admin/get-voice2voice-transcripts',
        queryParams
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          try {
            // Handle both response formats (with and without 'body' wrapper)
            const body: TranscriptResponseBody =
              'body' in response && response.body
                ? response.body
                : (response as TranscriptResponseBody);

            if (!body) {
              throw new Error('Invalid response format: missing body');
            }

            const data = body.data || [];
            const totalCount = body.totalCount || 0;
            const lastEvaluatedKey = body.lastEvaluatedKey || null;
            const averageRatingValue =
              body.averageRating != null ? Number(body.averageRating) : null;
            const ratingCountValue =
              body.ratingCount != null ? Number(body.ratingCount) : 0;

            if (!Array.isArray(data)) {
              throw new Error('Invalid response format: data is not an array');
            }

            // FIX: Parse messageHistory if it comes as stringified JSON
            const processedData: Transcript[] = data.map((transcript) =>
              this.normalizeTranscript(transcript)
            );

            this.transcripts.set(processedData);
            this.totalCount.set(totalCount);
            this.lastEvaluatedKey.set(lastEvaluatedKey);
            this.averageUserRating.set(
              Number.isFinite(averageRatingValue) ? averageRatingValue : null
            );
            this.userRatingCount.set(
              Number.isFinite(ratingCountValue) ? ratingCountValue : 0
            );
            this.hasSearchedOnce.set(true);
            this.isLoading.set(false);
          } catch (parseError) {
            console.error(
              'Error parsing transcript response:',
              parseError,
              'Response:',
              response
            );
            this.isLoading.set(false);
            const message =
              parseError instanceof Error
                ? parseError.message
                : 'Unknown error';
            this.errorMessage.set(
              `Failed to parse transcripts response: ${message}`
            );
            alert(`Error: Failed to parse transcripts response. ${message}`);
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error fetching transcripts:', err);
          const errorMessage =
            err.error?.message || err.message || 'Unknown error';
          const statusCode = err.status || 'N/A';
          this.isLoading.set(false);
          const message = `Failed to fetch transcripts (Status: ${statusCode}). ${errorMessage || 'Please try again.'}`;
          this.errorMessage.set(message);
          alert(message);
        },
      });
  }

  private normalizeTranscript(transcript: RawTranscript): Transcript {
    let messageHistory: Message[] = [];

    if (typeof transcript.messageHistory === 'string') {
      try {
        messageHistory = JSON.parse(transcript.messageHistory);
      } catch (e) {
        console.warn(
          'Failed to parse messageHistory for transcript',
          transcript.pk,
          e
        );
        messageHistory = [];
      }
    } else if (Array.isArray(transcript.messageHistory)) {
      messageHistory = transcript.messageHistory;
    }

    return { ...transcript, messageHistory };
  }

  loadNextPage() {
    if (!this.hasNextPage()) return;

    // Save current page's key for navigation
    if (this.lastEvaluatedKey()) {
      this.previousPages.update((pages) => [
        ...pages,
        this.lastEvaluatedKey()!,
      ]);
    }

    this.currentPage.update((p) => p + 1);
    this.fetchTranscripts();
  }

  loadPreviousPage() {
    if (!this.hasPreviousPage()) return;

    this.previousPages.update((pages) => {
      const newPages = [...pages];
      newPages.pop(); // Remove current page's key

      if (newPages.length > 0) {
        this.lastEvaluatedKey.set(newPages[newPages.length - 1]);
      } else {
        this.lastEvaluatedKey.set(null);
      }

      return newPages;
    });

    this.currentPage.update((p) => p - 1);
    this.fetchTranscripts();
  }

  hasNextPage(): boolean {
    return this.lastEvaluatedKey() !== null && this.transcripts().length > 0;
  }

  hasPreviousPage(): boolean {
    return this.previousPages().length > 0;
  }

  hasSearched(): boolean {
    return this.hasSearchedOnce();
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  formatDuration(milliseconds: number): string {
    if (!milliseconds) return '0s';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  }

  formatProductType(value: string): string {
    const type = this.productTypes.find((t) => t.value === value);
    return type ? type.label : value;
  }

  getMessageTimestamp(msg: Message): string | undefined {
    if (msg.timestamp) return msg.timestamp;
    if (
      msg.content &&
      typeof msg.content === 'object' &&
      !Array.isArray(msg.content) &&
      msg.content.timestamp
    ) {
      return msg.content.timestamp;
    }
    return undefined;
  }

  getMessageContent(msg: Message): string {
    const content = msg?.content;

    if (content == null) return '';
    if (typeof content === 'string') return content;

    // Bedrock/Nova Sonic content blocks: [{ text: '...' }, ...]
    if (Array.isArray(content)) {
      return content
        .map((block) => block.text ?? block.message ?? '')
        .filter((text) => text.trim().length > 0)
        .join(' ');
    }

    // Single content block: { text: '...' } or { message: '...' }
    if (content.text != null) {
      return content.text;
    }
    if (content.message != null) {
      return content.message;
    }

    return '';
  }

  async exportData(format: 'csv' | 'html') {
    if (this.isExporting()) return;

    const selectedColumns = this.exportColumns().filter((c) => c.selected);
    if (selectedColumns.length === 0) {
      alert('Select at least one column to export.');
      return;
    }

    this.isExporting.set(true);

    try {
      const { transcripts, truncated } =
        await this.fetchAllTranscriptsForExport();

      if (transcripts.length === 0) {
        alert('No transcripts found for the selected date range.');
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];

      if (format === 'csv') {
        const csv = this.buildExportCsv(transcripts, selectedColumns);
        this.downloadFile(
          csv,
          'text/csv',
          `voice2voice-transcripts-${timestamp}.csv`
        );
      } else {
        const html = this.buildExportHtmlReport(transcripts, selectedColumns);
        this.downloadFile(
          html,
          'text/html',
          `voice2voice-transcripts-${timestamp}.html`
        );
      }

      if (truncated) {
        alert(
          `Export capped at ${transcripts.length} transcripts. Narrow the date range to export the remaining results.`
        );
      }
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again with a smaller date range.');
    } finally {
      this.isExporting.set(false);
      this.exportProgress.set('');
    }
  }

  private async fetchAllTranscriptsForExport(): Promise<{
    transcripts: Transcript[];
    truncated: boolean;
  }> {
    const PAGE_LIMIT = 100;
    const MAX_PAGES = 20;

    const queryParams: Record<string, string> = { limit: String(PAGE_LIMIT) };

    if (this.exportStartDate) {
      queryParams['startDate'] = this.exportStartDate
        .toISOString()
        .split('T')[0];
    }

    if (this.exportEndDate) {
      const endDate = new Date(this.exportEndDate);
      endDate.setHours(23, 59, 59, 999);
      queryParams['endDate'] = endDate.toISOString();
    }

    const transcripts: Transcript[] = [];
    let lastEvaluatedKey: string | null = null;
    let page = 0;
    let truncated = false;

    do {
      const params = { ...queryParams };
      if (lastEvaluatedKey) {
        params['lastEvaluatedKey'] = lastEvaluatedKey;
      }

      page++;
      this.exportProgress.set(`Fetching page ${page}...`);

      const response = await firstValueFrom(
        this.apiService.get<TranscriptResponse | TranscriptResponseBody>(
          'admin/get-voice2voice-transcripts',
          params
        )
      );

      const body: TranscriptResponseBody =
        'body' in response && response.body
          ? response.body
          : (response as TranscriptResponseBody);

      const data = body?.data || [];
      transcripts.push(...data.map((t) => this.normalizeTranscript(t)));

      lastEvaluatedKey = body?.lastEvaluatedKey || null;

      if (page >= MAX_PAGES && lastEvaluatedKey) {
        truncated = true;
        break;
      }
    } while (lastEvaluatedKey);

    return { transcripts, truncated };
  }

  private getExportValue(transcript: Transcript, columnId: string): string {
    switch (columnId) {
      case 'pk':
        return transcript.pk;
      case 'sk':
        return this.formatDate(transcript.sk);
      case 'connectionId':
        return transcript.connectionId;
      case 'planNumber':
        return transcript.planNumber || '';
      case 'productType':
        return transcript.productType
          ? this.formatProductType(transcript.productType)
          : '';
      case 'brand':
        return transcript.brand || '';
      case 'chatDuration':
        return this.formatDuration(transcript.chatDuration);
      case 'messageCount':
        return String(transcript.messageHistory?.length || 0);
      case 'messageHistory':
        return (transcript.messageHistory || [])
          .map((m) => `${m.role}: ${this.getMessageContent(m)}`)
          .join(' | ');
      default:
        return '';
    }
  }

  private buildExportCsv(
    transcripts: Transcript[],
    columns: ExportColumn[]
  ): string {
    const header = columns.map((c) => this.csvEscape(c.label)).join(',');
    const rows = transcripts.map((t) =>
      columns.map((c) => this.csvEscape(this.getExportValue(t, c.id))).join(',')
    );
    return [header, ...rows].join('\n');
  }

  private csvEscape(value: string): string {
    const str = value ?? '';
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  }

  private buildExportHtmlReport(
    transcripts: Transcript[],
    columns: ExportColumn[]
  ): string {
    const cards = transcripts
      .map((t) => {
        const fields = columns
          .map((c) => {
            const value =
              c.id === 'messageHistory'
                ? this.renderMessageHistoryHtml(t.messageHistory)
                : this.escapeHtml(this.getExportValue(t, c.id) || '—');
            return `<div class="field"><div class="field-label">${this.escapeHtml(c.label)}</div><div class="field-value">${value}</div></div>`;
          })
          .join('');
        return `<div class="card">${fields}</div>`;
      })
      .join('');

    return `<!DOCTYPE html>
<html>
<head>
  <title>Voice2Voice Transcript Report</title>
  <meta charset="utf-8" />
  <style>
    body { font-family: sans-serif; padding: 40px; background: #f0f2f5; color: #222; }
    h1 { margin-bottom: 4px; }
    .subtitle { color: #666; margin-bottom: 24px; }
    .card { background: white; padding: 20px; margin-bottom: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border-left: 6px solid #667eea; }
    .field { margin-bottom: 12px; }
    .field-label { font-weight: bold; color: #667eea; text-transform: uppercase; font-size: 12px; margin-bottom: 4px; }
    .field-value { white-space: pre-wrap; }
    .msg { margin-bottom: 6px; }
    .msg .role { font-weight: 600; margin-right: 6px; }
    .msg.user .role { color: #1976d2; }
    .msg.assistant .role, .msg.agent .role { color: #388e3c; }
    .msg.system .role { color: #f57c00; }
  </style>
</head>
<body>
  <h1>Voice2Voice Transcript Report</h1>
  <p class="subtitle">${transcripts.length} transcript(s)</p>
  ${cards}
</body>
</html>`;
  }

  private renderMessageHistoryHtml(messages: Message[]): string {
    if (!messages || messages.length === 0) return '—';
    return messages
      .map(
        (m) =>
          `<div class="msg ${this.escapeHtml(m.role)}"><span class="role">${this.escapeHtml(m.role)}:</span>${this.escapeHtml(this.getMessageContent(m))}</div>`
      )
      .join('');
  }

  private escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  private downloadFile(content: string, type: string, filename: string) {
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  viewDetails(transcript: Transcript) {
    this.selectedTranscript.set(transcript);
  }

  closeDetails() {
    this.selectedTranscript.set(null);
  }

  viewFallbackTrace(transcript: Transcript) {
    this.dialog.open(VoiceFallbackDialogComponent, {
      width: '850px',
      maxHeight: '85vh',
      data: {
        customerId: transcript.pk,
        planNumber: transcript.planNumber,
        // Assumed to line up with the fallback endpoints' voiceSessionId
        // (one Nova Sonic voice session = one WebSocket connection) —
        // not confirmed against the backend. Used only to highlight the
        // matching row in the dialog.
        voiceSessionId: transcript.connectionId,
      },
    });
  }
}
